// nodeClipboard — the ONE implementation of "take a node subset out of graph A
// and put it into graph B additively, obeying the rules a human obeys".
//
// Four features are the same operation with different endpoints, and they all
// route through here so the rules cannot drift apart:
//
//   • editor Ctrl+C / Ctrl+X / Ctrl+V   — OS clipboard as the wire
//   • blueprint-asset card export        — a file as the wire
//   • drag-drop / File ▸ Import          — a file as the source
//   • the MCP's copy_nodes tool          — two AAM documents as the endpoints
//
// The alternative was four implementations of one rule set, which is exactly
// the failure this repo already paid for once: aos-mcp's vendored applyConnect
// implemented the DATA pin-capacity rule and silently dropped the EXEC one, so
// the MCP could author an exec fan-out no human can draw. A comment saying
// "keep in sync" is what failed there; a shared module plus a parity test is
// what holds.
//
// Leaf module: no imports, no browser globals, importable under `node --test`
// (same constraint as graphConnectPolicy.js / shortcutRegistry.js). The two
// things that DO vary by host — minting an id and knowing which assets exist —
// are injected by the caller rather than reached for.

// ── Envelope format ─────────────────────────────────────────────────────────
//
// One self-describing shape for every consumer. `scope` is the only field that
// distinguishes a whole card from a loose selection; nothing else differs.
//
// The tag's PRESENCE is how a file identifies itself — see
// assetUploadDispatch's content sniff. `.json` is already claimed by the
// face-clip wizard, so an extension cannot decide this and must not try.

export const CLIP_FORMAT = 'aosBlueprintClip'
export const CLIP_VERSION = 1

/** The closed set of graph kinds, matching assetManagerApi.GRAPH_KINDS. */
export const CLIP_KINDS = ['body', 'face', 'events']

/**
 * May a payload of `fromKind` be pasted into `toKind`?
 *
 * SAME KIND ONLY. A body graph pastes into a body graph, a face graph into a
 * face graph, an event graph into an event graph — nothing crosses.
 *
 * The three graphs are three different jobs, and a node that is meaningful in
 * one is noise in another: a `playClip` naming a body clip does nothing in the
 * face graph, a `liveLink` or `rigController` does nothing in the body graph,
 * and the event graph is a different runtime entirely (`EventRuntime` vs
 * `GraphEvaluator`) with a different node-type registry and a different id
 * namespace (`ev_` vs `node_`).
 *
 * body↔face was permitted at first, on the argument that BlueprintEditor mounts
 * both canvases over ONE `BUILTIN_NODE_TYPES` map so the types are technically
 * interchangeable. That is a fact about the implementation, not about the
 * author's intent: a shared renderer registry is why such a paste would not
 * crash, not a reason it should be allowed. The product rule is that a graph
 * accepts only its own nodes.
 *
 * Refusals name BOTH kinds, because the failure mode this prevents is a user
 * pressing Ctrl+V five times at a canvas that silently ignores them.
 */
export function canPaste(fromKind, toKind) {
  if (!CLIP_KINDS.includes(fromKind)) {
    return { ok: false, reason: `unknown source graph kind "${fromKind}"` }
  }
  if (!CLIP_KINDS.includes(toKind)) {
    return { ok: false, reason: `unknown target graph kind "${toKind}"` }
  }
  if (fromKind !== toKind) {
    return {
      ok: false,
      reason: `this clipboard holds ${fromKind} nodes, and the ${toKind} graph accepts only ${toKind} nodes`,
    }
  }
  return { ok: true }
}

// ── Character-scoped references ─────────────────────────────────────────────
//
// A copied node keeps its reference STRINGS. Whether they resolve is a property
// of the TARGET character, so it is reported at paste time, never baked in.
// Field names verified against blueprintNodeFactory's default data and the node
// components that read them.

const VARIABLE_FIELDS = ['varName', 'variableName', 'alphaVariable', 'boolVariable']
const CLIP_FIELDS = ['clipName']
const AUDIO_FIELDS = ['audioAssetId']
const FUNCTION_FIELDS = ['functionId']

/**
 * A clipName beginning with `__` is a runtime SENTINEL, not an asset:
 * `__livelink__`, `__rigcontrol__`, `__headfollow__`, `__eyefollow__`. Reporting
 * those as missing clips would put four permanent false positives on every
 * paste, which teaches people to ignore the report.
 */
export function isSentinelClip(name) {
  return typeof name === 'string' && name.startsWith('__')
}

/**
 * Walk a node list and every graph nested inside it, calling `visit(node)`.
 *
 * Nesting is deeper than it looks and all of it is real: a stateMachine's
 * `data.innerNodes` holds `state` nodes which have inner graphs of their own,
 * and its `data.innerEdges` carry `edge.data.ruleGraph` — a whole rule graph per
 * transition. A walker that stops at one level silently ignores most of a
 * copied state machine.
 */
export function walkNodes(nodes, visit) {
  for (const node of nodes ?? []) {
    visit(node)
    walkNodes(node?.data?.innerNodes, visit)
    for (const edge of node?.data?.innerEdges ?? []) {
      walkNodes(edge?.data?.ruleGraph?.nodes, visit)
    }
  }
}

function pushRef(set, value) {
  if (typeof value === 'string' && value.trim() !== '') set.add(value)
}

/**
 * The manifest of character-scoped things this payload needs. Computed, never
 * authored — so the editor's paste report and the MCP tool's response come from
 * one derivation instead of two that can disagree.
 */
export function collectRequires(nodes) {
  const clips = new Set()
  const audioAssetIds = new Set()
  const variables = new Set()
  const functions = new Set()

  walkNodes(nodes, (node) => {
    const data = node?.data
    if (!data || typeof data !== 'object') return
    for (const f of CLIP_FIELDS) if (!isSentinelClip(data[f])) pushRef(clips, data[f])
    for (const f of AUDIO_FIELDS) pushRef(audioAssetIds, data[f])
    for (const f of VARIABLE_FIELDS) pushRef(variables, data[f])
    for (const f of FUNCTION_FIELDS) pushRef(functions, data[f])
  })

  const sorted = (s) => Array.from(s).sort()
  return {
    clips: sorted(clips),
    audioAssetIds: sorted(audioAssetIds),
    variables: sorted(variables),
    functions: sorted(functions),
  }
}

// ── Extraction ──────────────────────────────────────────────────────────────

// Render-time injections that must never reach the clipboard: handler
// callbacks and the whole-graph context the editors pass down. EventGraph's own
// duplicateNode strips exactly these three, for the same reason.
const TRANSIENT_DATA_KEYS = ['onChange', 'functions', 'fnDef']
// ReactFlow interaction state. Pasting a node with selected:true fights the
// paste-selects-what-it-pasted behaviour.
const TRANSIENT_NODE_KEYS = ['selected', 'dragging', 'measured', 'positionAbsolute', 'width', 'height']

function cleanNode(node) {
  const out = {}
  for (const [k, v] of Object.entries(node)) {
    if (TRANSIENT_NODE_KEYS.includes(k)) continue
    out[k] = v
  }
  if (node.data && typeof node.data === 'object') {
    const data = {}
    for (const [k, v] of Object.entries(node.data)) {
      if (TRANSIENT_DATA_KEYS.includes(k)) continue
      if (typeof v === 'function') continue
      data[k] = v
    }
    out.data = data
  }
  return out
}

/** Deep structural copy. JSON round-trip also drops any function we missed. */
function deepCopy(value) {
  return value === undefined ? undefined : JSON.parse(JSON.stringify(value))
}

/**
 * Build an envelope from a node subset.
 *
 * An edge is copied only when BOTH endpoints are in the selection: a half-edge
 * would land pointing at a node that is not there, which every downstream
 * consumer would have to special-case.
 *
 * `declarations` carries only what `requires` actually names — copying three
 * nodes must not drag forty of a character's variables along with them.
 */
export function extractSelection(graph, nodeIds, { kind, source = {}, variables = [], functions = {}, scope = 'selection' } = {}) {
  if (!CLIP_KINDS.includes(kind)) throw new Error(`extractSelection: unknown kind "${kind}"`)
  const wanted = new Set(nodeIds ?? [])
  const picked = (graph?.nodes ?? []).filter(n => wanted.has(n.id))
  if (picked.length === 0) throw new Error('extractSelection: no nodes selected')

  const nodes = picked.map(n => deepCopy(cleanNode(n)))
  const ids = new Set(nodes.map(n => n.id))
  const edges = (graph?.edges ?? [])
    .filter(e => ids.has(e.source) && ids.has(e.target))
    .map(e => deepCopy(e))

  const requires = collectRequires(nodes)
  const neededVars = new Set(requires.variables)
  const neededFns = new Set(requires.functions)

  return {
    [CLIP_FORMAT]: CLIP_VERSION,
    kind,
    scope,
    source: deepCopy({ exportedAt: new Date().toISOString(), ...source }),
    graph: { nodes, edges },
    declarations: {
      variables: (variables ?? []).filter(v => neededVars.has(v?.name)).map(deepCopy),
      functions: Object.fromEntries(
        Object.entries(functions ?? {})
          .filter(([id]) => neededFns.has(id))
          .map(([id, def]) => [id, deepCopy(def)]),
      ),
    },
    requires,
  }
}

/**
 * Build an envelope from a WHOLE graph — the card export. Same shape as a
 * selection, and `scope` says which, so one reader handles both.
 *
 * `graph` is normalised to { nodes, edges } here, including for the events kind
 * whose document stores eventNodes/eventEdges. Denormalising happens once, at
 * the write boundary, the way blueprintDoc.getGraphView already does it.
 *
 * Declarations differ from a selection's on purpose: a card export carries the
 * document's FULL set, not the reference-filtered subset. The file is the
 * graph, and a variable declared but not yet wired is still part of it — a
 * round-trip that dropped it would quietly delete the author's work.
 */
export function exportGraphEnvelope(graph, { kind, source = {}, variables = [], functions = {} } = {}) {
  const nodes = graph?.nodes ?? graph?.eventNodes ?? []
  const edges = graph?.edges ?? graph?.eventEdges ?? []
  const env = extractSelection({ nodes, edges }, nodes.map(n => n.id), { kind, source, scope: 'graph' })
  env.declarations = {
    variables: deepCopy(variables ?? []),
    functions: deepCopy(functions ?? {}),
  }
  return env
}

// ── Parsing ─────────────────────────────────────────────────────────────────

/**
 * Validate an envelope from text or an object.
 *
 * Every rejection names what was wrong. A generic throw here would surface to
 * the user as "import failed" on a file they cannot inspect.
 *
 * Ordinary text that is not one of ours returns `notOurs: true` rather than an
 * error — pasting a Slack message into a canvas is a miss, not a failure, and
 * must not raise a dialog.
 */
export function parseEnvelope(input) {
  let obj = input
  if (typeof input === 'string') {
    const trimmed = input.trim()
    if (trimmed === '') return { ok: false, notOurs: true, reason: 'clipboard is empty' }
    // A payload of ours is always a JSON object. Anything that is not even that
    // is someone else's content.
    if (!trimmed.startsWith('{')) return { ok: false, notOurs: true, reason: 'not JSON' }
    try {
      obj = JSON.parse(trimmed)
    } catch (e) {
      return { ok: false, notOurs: true, reason: `not valid JSON (${e.message})` }
    }
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return { ok: false, notOurs: true, reason: 'not a JSON object' }
  }
  const version = obj[CLIP_FORMAT]
  if (version === undefined) {
    return { ok: false, notOurs: true, reason: `not a blueprint clipboard payload (no "${CLIP_FORMAT}" tag)` }
  }
  // From here on it CLAIMS to be ours, so every failure is a real error the
  // user needs to see rather than a silent miss.
  if (version !== CLIP_VERSION) {
    return {
      ok: false,
      reason: `this payload is format version ${version}; this build reads version ${CLIP_VERSION}`,
    }
  }
  if (!CLIP_KINDS.includes(obj.kind)) {
    return { ok: false, reason: `payload names an unknown graph kind "${obj.kind}"` }
  }
  if (obj.scope !== 'graph' && obj.scope !== 'selection') {
    return { ok: false, reason: `payload has an unknown scope "${obj.scope}"` }
  }
  const nodes = obj.graph?.nodes
  if (!Array.isArray(nodes)) {
    return { ok: false, reason: 'payload carries no graph.nodes array' }
  }
  if (nodes.length === 0) {
    return { ok: false, reason: 'payload carries no nodes' }
  }
  if (!Array.isArray(obj.graph?.edges)) {
    return { ok: false, reason: 'payload carries no graph.edges array' }
  }
  return { ok: true, envelope: obj }
}

export function serializeEnvelope(envelope) {
  return JSON.stringify(envelope, null, 2)
}

// ── Id remapping ────────────────────────────────────────────────────────────

/**
 * `node_N` / `ev_N` — the two id schemes nextNodeId mints. Used to SEED a
 * minter (see maxOwnedId), not to decide what may be renamed.
 */
const OWNED_ID_RE = /^(node|ev)_\d+$/

export function isOwnedId(id) {
  return typeof id === 'string' && OWNED_ID_RE.test(id)
}

/**
 * Ids that must survive a remap because code matches them as LITERAL STRINGS.
 *
 * Only `entry` qualifies, and only because it lives in a CONTAINER's own
 * namespace: a function body and a state machine both seed one
 * (`{ id: 'entry', deletable: false }`), EventGraph's delete and duplicate both
 * guard on `id === 'entry'`, so renaming it produces a container whose locked
 * node can never be found again. Per-container namespaces mean preserving it is
 * also collision-free.
 *
 * `final` is deliberately NOT reserved even though BlueprintEditor and OGSModel
 * both match `n.id === 'final'`: it is the TOP-LEVEL graph output, of which
 * there is exactly one, so preserving it across a paste would duplicate the id
 * outright. A pasted copy becomes an ordinary node, which is the correct
 * outcome — it is not the output.
 */
const RESERVED_IDS = new Set(['entry'])

/**
 * May this id be replaced with a freshly minted one?
 *
 * EVERYTHING except a reserved literal. The earlier rule was "only rename ids
 * matching node_N / ev_N", which was wrong on real data and shipped a duplicate
 * on the first browser run: the shipped blueprints use human-authored semantic
 * ids (`base_idle_sm`, `blend_b`, `conv_sm`, `final`), so nothing was renamed
 * and a paste produced two nodes sharing every id. Uniqueness is the whole job
 * of a remap, so the default has to be "rename", with a named exception list —
 * not the reverse.
 */
export function shouldRemapId(id) {
  return typeof id === 'string' && id !== '' && !RESERVED_IDS.has(id)
}

/**
 * Highest `<prefix>_N` in a node list, recursing into every nested graph.
 *
 * A minter MUST be seeded from this over the LIVE graph, not over a mount-time
 * snapshot. The body editor's own `nextId` seeds `_nodeId` from
 * `saved.current` — the localStorage copy read once at mount — so when a
 * `?blueprint=` URL (or a cloud load) replaces the graph with one whose ids run
 * higher, it hands back ids that already exist. A single add per render mostly
 * gets away with it; a multi-node paste lands duplicates immediately.
 *
 * Recursion matters for the same reason maxNodeId recurses: a state machine's
 * innerNodes are minted from this same pool, so ignoring them lets a paste
 * collide with a node nested inside an existing container.
 */
export function maxOwnedId(nodes, prefix) {
  const re = new RegExp('^' + prefix + '_([0-9]+)$')
  let max = 0
  walkNodes(nodes, (n) => {
    const m = re.exec(n?.id ?? '')
    if (m) max = Math.max(max, Number.parseInt(m[1], 10))
  })
  return max
}

/**
 * A fresh monotonic minter for ONE paste, seeded past everything already in the
 * graph. `floor` lets a host that also keeps a global counter (the events
 * graph's `window.__evNodeId__`) stay ahead of that too, so ids remain unique
 * across both mechanisms.
 */
export function createMinter(nodes, prefix, { floor = 0 } = {}) {
  let n = Math.max(maxOwnedId(nodes, prefix) + 1, floor, 1)
  return () => `${prefix}_${n++}`
}

function remapGraphIds(nodes, edges, mint, idMap) {
  // Pass 1: mint every owned id in this namespace first, so an edge can be
  // rewritten regardless of the order its endpoints appear in.
  for (const n of nodes ?? []) {
    if (shouldRemapId(n.id) && !idMap.has(n.id)) idMap.set(n.id, mint())
  }
  const to = (id) => idMap.get(id) ?? id

  const outNodes = (nodes ?? []).map((n) => {
    const node = { ...n, id: to(n.id) }
    if (n.data && typeof n.data === 'object') {
      const data = { ...n.data }
      // Nested graphs get their OWN remap pass. A partially-remapped container
      // is a graph whose inner edges point at the SOURCE character's ids —
      // populated, plausible, and past every empty-graph guard.
      if (Array.isArray(data.innerNodes) || Array.isArray(data.innerEdges)) {
        const inner = remapGraphIds(data.innerNodes ?? [], data.innerEdges ?? [], mint, new Map())
        data.innerNodes = inner.nodes
        data.innerEdges = inner.edges
      }
      node.data = data
    }
    return node
  })

  const outEdges = (edges ?? []).map((e) => {
    const edge = {
      ...e,
      source: to(e.source),
      target: to(e.target),
    }
    // An edge id is regenerated from its (possibly new) endpoints so two pasted
    // copies of one selection cannot share an edge id — ReactFlow keys on it.
    edge.id = `e-${edge.source}.${e.sourceHandle ?? ''}-${edge.target}.${e.targetHandle ?? ''}`
    // A transition edge carries a whole rule graph, in its own namespace.
    if (e.data?.ruleGraph) {
      const rule = remapGraphIds(e.data.ruleGraph.nodes ?? [], e.data.ruleGraph.edges ?? [], mint, new Map())
      edge.data = { ...e.data, ruleGraph: { nodes: rule.nodes, edges: rule.edges } }
    }
    return edge
  })

  return { nodes: outNodes, edges: outEdges, idMap }
}

/**
 * Fresh ids for a payload about to be pasted, plus an optional position offset.
 *
 * `mintId` is INJECTED because the two hosts count differently: the body/face
 * editor derives `node_N` from maxNodeId over the live graph, the events editor
 * pulls `ev_N` off a window counter, and the MCP calls nextNodeId on a document.
 * Reimplementing any of those here would be a third counter that drifts.
 *
 * Returns the remapped nodes/edges and the idMap, which callers report so a
 * user (or an agent) can see what the pasted nodes are now called.
 */
export function remapIds(envelope, { mintId, offset = { x: 0, y: 0 } } = {}) {
  if (typeof mintId !== 'function') throw new Error('remapIds: mintId function required')
  const idMap = new Map()
  const { nodes, edges } = remapGraphIds(
    deepCopy(envelope.graph?.nodes ?? []),
    deepCopy(envelope.graph?.edges ?? []),
    mintId,
    idMap,
  )
  const moved = nodes.map(n => ({
    ...n,
    position: {
      x: (n.position?.x ?? 0) + (offset.x ?? 0),
      y: (n.position?.y ?? 0) + (offset.y ?? 0),
    },
  }))
  return { nodes: moved, edges, idMap }
}

// ── Reference resolution ────────────────────────────────────────────────────

/**
 * Diff what the payload needs against what the TARGET character has.
 *
 * `available` is supplied by the caller — the editor reads it off the live asset
 * registry, the MCP off the target document — because this module must not know
 * how to reach either.
 *
 * `resolvable` is the subset of missing variables/functions the payload's own
 * `declarations` can supply, which is what makes the paste report offer an
 * action instead of just naming a problem. Clips and audio are never resolvable:
 * we cannot invent an asset, and a fabricated row would be exactly the fake data
 * the UX rules forbid.
 */
export function resolveRequires(envelope, available = {}) {
  const req = envelope.requires ?? collectRequires(envelope.graph?.nodes ?? [])
  const has = (list) => new Set(list ?? [])

  const haveClips = has(available.clips)
  const haveAudio = has(available.audioAssetIds)
  const haveVars = has(available.variables)
  const haveFns = has(available.functions)

  const missing = {
    clips: req.clips.filter(c => !haveClips.has(c)),
    audioAssetIds: req.audioAssetIds.filter(a => !haveAudio.has(a)),
    variables: req.variables.filter(v => !haveVars.has(v)),
    functions: req.functions.filter(f => !haveFns.has(f)),
  }

  const declaredVars = new Set((envelope.declarations?.variables ?? []).map(v => v?.name))
  const declaredFns = new Set(Object.keys(envelope.declarations?.functions ?? {}))

  return {
    missing,
    resolvable: {
      variables: missing.variables.filter(v => declaredVars.has(v)),
      functions: missing.functions.filter(f => declaredFns.has(f)),
    },
    total: missing.clips.length + missing.audioAssetIds.length
      + missing.variables.length + missing.functions.length,
  }
}

/**
 * One human sentence per class, for the paste report. Returns null when
 * everything resolved — the caller shows nothing rather than "0 problems".
 */
export function describeMissing(resolution) {
  if (!resolution || resolution.total === 0) return null
  const parts = []
  const say = (list, one, many) => {
    if (list.length === 0) return
    parts.push(`${list.length} ${list.length === 1 ? one : many} not on this character: ${list.join(', ')}`)
  }
  say(resolution.missing.clips, 'clip', 'clips')
  say(resolution.missing.audioAssetIds, 'audio asset', 'audio assets')
  say(resolution.missing.variables, 'variable', 'variables')
  say(resolution.missing.functions, 'function', 'functions')
  return parts.join('; ')
}
